/**
 * One competition's page (wave 15, card 3d9c00e6): `GET /api/v1/analytics/competitions/{id}`
 * and its four tab routes, `…/funnel`, `…/sources`, `…/devices` and `…/advertising`
 * (`modules/competition_detail`, whose README is the contract). `analytics.read` for all
 * five.
 *
 * The main route's `summary` is the competitions list's row for the same competition and
 * period, field for field, so it is read with the list's own mapper
 * (`competitionRowFromWire`) and keeps the list's units: integer minor units of
 * `currency`, the ticket price in its own `ticketPriceCurrency`. Every other amount here
 * is converted to major units of `currency` by its mapper, as the Overview's are, so a
 * chart or a formatter takes it as it is.
 *
 * Every figure the API cannot give arrives as `null` and stays `null`: one it names in a
 * `not_available` reads "Not live yet" (or "Not available") with the reason, any other
 * `null` is undefined (no orders, so no AOV) and reads "—". Never 0 in its place.
 */

import { majorUnits } from '@/design/format'
import type { GlobalFilters } from '@/lib/filters'

import {
  competitionRowFromWire,
  notAvailableFromWire,
  UNAVAILABLE_REASONS,
  type CompetitionRow,
  type CompetitionRowOut,
  type CompetitionsCoverage,
  type CompetitionsList,
} from './competitions'
import { overviewSearch, type OverviewMetaOut } from './overview'
import {
  ATTRIBUTION_MODELS,
  FUNNEL_STAGES,
  type AttributionModel,
  type Envelope,
  type FunnelStageKey,
  type PacingStatus,
} from './types'

/** Why a figure has no value (`definitions.py` `NotAvailable`): the list's three, then the page's own. */
export const DETAIL_UNAVAILABLE_REASONS = [
  ...UNAVAILABLE_REASONS,
  'not_per_competition',
  'no_spend_per_competition',
  'no_price_history',
  'not_on_sale_in_range',
  'no_capacity',
  'no_schedule',
  'not_on_sale',
] as const
export type DetailUnavailableReason = (typeof DETAIL_UNAVAILABLE_REASONS)[number]

/** `granularity` on the main route: a point per day, or per seven days counted from `from`. */
export const SALES_GRANULARITIES = ['day', 'week'] as const
export type SalesGranularity = (typeof SALES_GRANULARITIES)[number]

/** The model the sources route defaults to (the Attribution page's, blueprint 05 §5.8). */
export const DEFAULT_SOURCES_MODEL: AttributionModel = 'last_non_direct'

/** The devices the Devices tab lists, in order (`definitions.py` `DEVICES`); crawlers are counted apart. */
export const DEVICE_LABELS: Readonly<Record<string, string>> = {
  desktop: 'Desktop',
  mobile: 'Mobile',
  tablet: 'Tablet',
  other: 'Other',
  unknown: 'Unknown',
}

export const TIMELINE_KINDS = ['status_changed', 'started', 'campaign_started', 'campaign_ended', 'ended', 'draw'] as const
export type TimelineKind = (typeof TIMELINE_KINDS)[number]

// --- the view ----------------------------------------------------------------------

/** When the competition's lifetime figures stand against its dates, now. */
export interface CompetitionSchedule {
  /** Tenant days to `endAt`'s day: 0 on the day, negative after it; null without the date. */
  daysToEnd: number | null
  daysToDraw: number | null
  /** Tickets a day over the period's last seven complete days on sale. */
  velocityPerDay: number | null
  /** The days the velocity is over, both inclusive (tenant days); null with no velocity. */
  velocityFrom: string | null
  velocityTo: string | null
  velocityDays: number
  /** Lifetime sold against a straight line from none at the start to capacity at the end. */
  pacing: PacingStatus | null
  /** Where that line is now, in tickets. */
  pacingExpectedSold: number | null
}

export interface SalesPoint {
  /** The bucket's first and last tenant day (the same day at `day`). */
  date: string
  endDate: string
  /** Major units of the response's currency. */
  revenue: number
  tickets: number
  orders: number
}

export interface SalesTotals {
  revenue: number
  tickets: number
  orders: number
}

export interface CompetitionSales {
  granularity: SalesGranularity
  /** Every bucket of the period, zero-filled. */
  series: SalesPoint[]
  /** The series summed: the summary's revenue, tickets sold in range and orders. */
  totals: SalesTotals
}

export interface FirstTouchChannel {
  /** Null for the buyers with no first touch known: the "Unattributed" row, last. */
  channel: string | null
  label: string
  customers: number
}

export interface CompetitionCustomers {
  newCustomers: number
  returningCustomers: number
  /** Orders with no customer (guest checkouts): orders, not people. */
  guestOrders: number
  /** Major units: the period's revenue by whose order it was. */
  revenue: { new: number; returning: number; guest: number }
  firstTouchChannels: FirstTouchChannel[]
}

export interface TimelineEntry {
  /** A UTC instant. */
  at: string
  kind: TimelineKind
  /** `status_changed`: the status entered. */
  status: string | null
  campaignId: string | null
  campaignName: string | null
  campaignStatus: string | null
}

export interface CompetitionTimeline {
  /** The status as at the period's start; null when it had not opened. */
  statusAtStart: string | null
  /** What happened in the period, oldest first. */
  entries: TimelineEntry[]
}

/**
 * The main route's `not_available`, by what it is about. Its `advertising` entry is left
 * out: the Advertising tab reads its own route, whose four figures each name their reason.
 */
export interface DetailNotAvailable {
  /** The summary's columns: the list's reasons, passed through with the row. */
  summary: CompetitionsList['notAvailable']
  velocityPerDay?: DetailUnavailableReason
  pacing?: DetailUnavailableReason
  /** The timeline's price changes: always `no_price_history`. */
  priceChanges?: DetailUnavailableReason
}

/** What every tab's figures are framed by: the response's currency and the tenant's timezone. */
export interface DetailFrame {
  /** The currency every amount but the ticket price is in: `meta.currency`. */
  currency: string
  /** The tenant's timezone, which its days (and the period) are in. */
  timezone: string
}

export interface CompetitionDetail extends DetailFrame {
  summary: CompetitionRow
  schedule: CompetitionSchedule
  sales: CompetitionSales
  customers: CompetitionCustomers
  timeline: CompetitionTimeline
  notAvailable: DetailNotAvailable
  coverage: CompetitionsCoverage
}

export interface CompetitionFunnelStage {
  key: FunnelStageKey
  label: string
  users: number | null
  sessions: number | null
  stageConversionRate: number | null
  totalConversionRate: number | null
  dropOff: number | null
  dropOffRate: number | null
  /** Why the stage has no figures (`visitors` always: its events name no competition). */
  unavailable: DetailUnavailableReason | null
  /** No session reached it although a later stage was: its event is not sent, so its 0 is no drop-off. */
  withoutEvents: boolean
}

export interface CompetitionFunnel extends DetailFrame {
  /** In `FUNNEL_STAGES` order. */
  stages: CompetitionFunnelStage[]
  coverage: CompetitionsCoverage
}

export interface SourceFigures {
  orders: number
  tickets: number
  /** Major units. */
  revenue: number
}

export interface SourceRow extends SourceFigures {
  /** The credited touch's own fields, as stored; null when it has none. */
  source: string | null
  medium: string | null
  campaign: string | null
}

export interface ChannelSources extends SourceFigures {
  /** Null for the unattributed row: orders the model credits to no touch. */
  channel: string | null
  label: string
  /** A fraction of the competition's revenue; null with none. */
  revenueShare: number | null
  sources: SourceRow[]
}

export interface CompetitionSources extends DetailFrame {
  model: AttributionModel
  /** Most revenue first; the unattributed row last. */
  channels: ChannelSources[]
  totals: SourceFigures
}

export interface DeviceRow {
  device: string
  viewingSessions: number | null
  purchasingSessions: number | null
  convertedSessions: number | null
  /** Converted ÷ viewing; null with no viewing session. */
  conversionRate: number | null
}

export type DeviceField = Exclude<keyof DeviceRow, 'device'> | 'botSessions'

export interface CompetitionDevices extends DetailFrame {
  devices: DeviceRow[]
  /** Crawler sessions, left out of every row. */
  botSessions: number | null
  notAvailable: Partial<Record<DeviceField, DetailUnavailableReason>>
  coverage: CompetitionsCoverage
}

export interface CampaignChannel {
  channelId: string
  name: string
  channelType: string
  kind: string
  status: string
  startsAt: string | null
  endsAt: string | null
}

export interface PromotingCampaign {
  campaignId: string
  name: string
  status: string
  objective: string
  startsAt: string | null
  endsAt: string | null
  /** How many competitions it promotes, this one included. */
  competitionsPromoted: number
  channels: CampaignChannel[]
}

export type AdvertisingField = 'adSpend' | 'attributedRevenue' | 'cpa' | 'roas'

export interface CompetitionAdvertising extends DetailFrame {
  campaigns: PromotingCampaign[]
  /** Never given per competition: each null, `no_spend_per_competition`. Major units where money. */
  adSpend: number | null
  attributedRevenue: number | null
  cpa: number | null
  roas: number | null
  notAvailable: Partial<Record<AdvertisingField, DetailUnavailableReason>>
}

export interface CompetitionDetailQuery {
  /** The Suite's id (`cmp_…`), the list row's `competitionId`. */
  competitionId: string
  /** The global filters: the period, the website and the environments. */
  filters: GlobalFilters
}

export interface CompetitionDetailApi {
  /** The summary, schedule, sales series, customers and timeline: the page's first paint. */
  competitionDetail(query: CompetitionDetailQuery & { granularity?: SalesGranularity }): Promise<Envelope<CompetitionDetail>>
  competitionFunnel(query: CompetitionDetailQuery): Promise<Envelope<CompetitionFunnel>>
  competitionSources(query: CompetitionDetailQuery & { model?: AttributionModel }): Promise<Envelope<CompetitionSources>>
  competitionDevices(query: CompetitionDetailQuery): Promise<Envelope<CompetitionDevices>>
  competitionAdvertising(query: CompetitionDetailQuery): Promise<Envelope<CompetitionAdvertising>>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

interface CoverageOut {
  first_event_at: string | null
  measured_from: string | null
  note: string
}

export interface CompetitionDetailOut {
  data: {
    summary: CompetitionRowOut
    schedule: {
      days_to_end: number | null
      days_to_draw: number | null
      velocity_per_day: number | null
      velocity_from: string | null
      velocity_to: string | null
      velocity_days: number
      pacing: PacingStatus | null
      pacing_expected_sold: number | null
    }
    sales: {
      granularity: SalesGranularity
      series: { date: string; end_date: string; revenue_gross_minor: number; tickets: number; orders: number }[]
      totals: { revenue_gross_minor: number; tickets: number; orders: number }
    }
    customers: {
      new_customers: number
      returning_customers: number
      guest_orders: number
      revenue: { new_minor: number; returning_minor: number; guest_minor: number }
      first_touch_channels: { channel: string | null; label: string; customers: number }[]
    }
    timeline: {
      status_at_start: string | null
      entries: {
        at: string
        kind: string
        status?: string | null
        campaign_id?: string | null
        campaign_name?: string | null
        campaign_status?: string | null
      }[]
    }
    not_available: Record<string, string>
    coverage: CoverageOut
  }
  meta: OverviewMetaOut
}

export interface CompetitionFunnelOut {
  data: {
    stages: {
      stage: string
      label: string
      users: number | null
      sessions: number | null
      stage_conversion_rate: number | null
      total_conversion_rate: number | null
      drop_off: number | null
      drop_off_rate: number | null
    }[]
    stages_without_events: string[]
    not_available: Record<string, string>
    coverage: CoverageOut
  }
  meta: OverviewMetaOut
}

interface SourceFiguresOut {
  orders: number
  tickets: number
  revenue_minor: number
}

export interface CompetitionSourcesOut {
  data: {
    model: AttributionModel
    channels: (SourceFiguresOut & {
      channel: string | null
      label: string
      revenue_share: number | null
      sources: (SourceFiguresOut & { source: string | null; medium: string | null; campaign: string | null })[]
    })[]
    totals: SourceFiguresOut
  }
  meta: OverviewMetaOut
}

export interface CompetitionDevicesOut {
  data: {
    devices: {
      device: string
      viewing_sessions: number | null
      purchasing_sessions: number | null
      converted_sessions: number | null
      conversion_rate: number | null
    }[]
    bot_sessions: number | null
    not_available: Record<string, string>
    coverage: CoverageOut
  }
  meta: OverviewMetaOut
}

interface DatedOut {
  starts_at: string | null
  ends_at: string | null
}

export interface CompetitionAdvertisingOut {
  data: {
    campaigns: (DatedOut & {
      campaign_id: string
      name: string
      status: string
      objective: string
      competitions_promoted: number
      channels: (DatedOut & { channel_id: string; name: string; channel_type: string; kind: string; status: string })[]
    })[]
    ad_spend_minor: number | null
    attributed_revenue_minor: number | null
    cpa_minor: number | null
    roas: number | null
    not_available: Record<string, string>
  }
  meta: OverviewMetaOut
}

// --- wire → view -------------------------------------------------------------

function isReason(value: string | undefined): value is DetailUnavailableReason {
  return value !== undefined && (DETAIL_UNAVAILABLE_REASONS as readonly string[]).includes(value)
}

/** A reason the client knows, else none: a later API's new reason leaves its `null` reading "—", never a guess. */
function reasonOf(notAvailable: Record<string, string>, key: string): DetailUnavailableReason | undefined {
  const reason = Object.hasOwn(notAvailable, key) ? notAvailable[key] : undefined
  return isReason(reason) ? reason : undefined
}

/** `not_available` re-keyed by view field: each wire key that names a known reason, and nothing else. */
function reasonsFor<Field extends string>(
  notAvailable: Record<string, string>,
  fields: Readonly<Record<string, Field>>,
): Partial<Record<Field, DetailUnavailableReason>> {
  const out: Partial<Record<Field, DetailUnavailableReason>> = {}
  for (const [wire, field] of Object.entries(fields)) {
    const reason = reasonOf(notAvailable, wire)
    if (reason) out[field] = reason
  }
  return out
}

function coverageFromWire(coverage: CoverageOut): CompetitionsCoverage {
  return { firstEventAt: coverage.first_event_at, measuredFrom: coverage.measured_from, note: coverage.note }
}

function frameOf(meta: OverviewMetaOut): DetailFrame {
  return { currency: meta.currency, timezone: meta.timezone }
}

function isTimelineKind(kind: string): kind is TimelineKind {
  return (TIMELINE_KINDS as readonly string[]).includes(kind)
}

export function competitionDetailFromWire(body: CompetitionDetailOut): CompetitionDetail {
  const { data, meta } = body
  const { currency } = meta
  const major = (minor: number) => majorUnits(minor, currency)
  const { schedule, sales, customers, timeline } = data
  return {
    ...frameOf(meta),
    summary: competitionRowFromWire(data.summary),
    schedule: {
      daysToEnd: schedule.days_to_end,
      daysToDraw: schedule.days_to_draw,
      velocityPerDay: schedule.velocity_per_day,
      velocityFrom: schedule.velocity_from,
      velocityTo: schedule.velocity_to,
      velocityDays: schedule.velocity_days,
      pacing: schedule.pacing,
      pacingExpectedSold: schedule.pacing_expected_sold,
    },
    sales: {
      granularity: sales.granularity,
      series: sales.series.map((point) => ({
        date: point.date,
        endDate: point.end_date,
        revenue: major(point.revenue_gross_minor),
        tickets: point.tickets,
        orders: point.orders,
      })),
      totals: { revenue: major(sales.totals.revenue_gross_minor), tickets: sales.totals.tickets, orders: sales.totals.orders },
    },
    customers: {
      newCustomers: customers.new_customers,
      returningCustomers: customers.returning_customers,
      guestOrders: customers.guest_orders,
      revenue: {
        new: major(customers.revenue.new_minor),
        returning: major(customers.revenue.returning_minor),
        guest: major(customers.revenue.guest_minor),
      },
      firstTouchChannels: customers.first_touch_channels.map(({ channel, label, customers: count }) => ({
        channel,
        label,
        customers: count,
      })),
    },
    timeline: {
      statusAtStart: timeline.status_at_start,
      // A kind a later API adds is left out rather than shown under a wrong heading.
      entries: timeline.entries.flatMap((entry) =>
        isTimelineKind(entry.kind)
          ? [
              {
                at: entry.at,
                kind: entry.kind,
                status: entry.status ?? null,
                campaignId: entry.campaign_id ?? null,
                campaignName: entry.campaign_name ?? null,
                campaignStatus: entry.campaign_status ?? null,
              },
            ]
          : [],
      ),
    },
    notAvailable: {
      summary: notAvailableFromWire(data.not_available),
      ...reasonsFor(data.not_available, {
        velocity_per_day: 'velocityPerDay',
        pacing: 'pacing',
        price_changes: 'priceChanges',
      }),
    },
    coverage: coverageFromWire(data.coverage),
  }
}

function isStage(key: string): key is FunnelStageKey {
  return (FUNNEL_STAGES as readonly string[]).includes(key)
}

export function competitionFunnelFromWire(body: CompetitionFunnelOut): CompetitionFunnel {
  const { data } = body
  const missing = new Set(data.stages_without_events)
  return {
    ...frameOf(body.meta),
    // A stage a later API adds has no place in the funnel's order: left out, not appended.
    stages: data.stages.flatMap((stage) =>
      isStage(stage.stage)
        ? [
            {
              key: stage.stage,
              label: stage.label,
              users: stage.users,
              sessions: stage.sessions,
              stageConversionRate: stage.stage_conversion_rate,
              totalConversionRate: stage.total_conversion_rate,
              dropOff: stage.drop_off,
              dropOffRate: stage.drop_off_rate,
              unavailable: reasonOf(data.not_available, stage.stage) ?? null,
              withoutEvents: missing.has(stage.stage),
            },
          ]
        : [],
    ),
    coverage: coverageFromWire(data.coverage),
  }
}

export function competitionSourcesFromWire(body: CompetitionSourcesOut): CompetitionSources {
  const { data, meta } = body
  const figures = (row: SourceFiguresOut): SourceFigures => ({
    orders: row.orders,
    tickets: row.tickets,
    revenue: majorUnits(row.revenue_minor, meta.currency),
  })
  return {
    ...frameOf(meta),
    model: data.model,
    channels: data.channels.map((row) => ({
      ...figures(row),
      channel: row.channel,
      label: row.label,
      revenueShare: row.revenue_share,
      sources: row.sources.map((source) => ({
        ...figures(source),
        source: source.source,
        medium: source.medium,
        campaign: source.campaign,
      })),
    })),
    totals: figures(data.totals),
  }
}

export function competitionDevicesFromWire(body: CompetitionDevicesOut): CompetitionDevices {
  const { data } = body
  return {
    ...frameOf(body.meta),
    devices: data.devices.map((row) => ({
      device: row.device,
      viewingSessions: row.viewing_sessions,
      purchasingSessions: row.purchasing_sessions,
      convertedSessions: row.converted_sessions,
      conversionRate: row.conversion_rate,
    })),
    botSessions: data.bot_sessions,
    notAvailable: reasonsFor(data.not_available, {
      viewing_sessions: 'viewingSessions',
      purchasing_sessions: 'purchasingSessions',
      converted_sessions: 'convertedSessions',
      conversion_rate: 'conversionRate',
      bot_sessions: 'botSessions',
    }),
    coverage: coverageFromWire(data.coverage),
  }
}

export function competitionAdvertisingFromWire(body: CompetitionAdvertisingOut): CompetitionAdvertising {
  const { data, meta } = body
  const major = (minor: number | null) => (minor === null ? null : majorUnits(minor, meta.currency))
  return {
    ...frameOf(meta),
    campaigns: data.campaigns.map((campaign) => ({
      campaignId: campaign.campaign_id,
      name: campaign.name,
      status: campaign.status,
      objective: campaign.objective,
      startsAt: campaign.starts_at,
      endsAt: campaign.ends_at,
      competitionsPromoted: campaign.competitions_promoted,
      channels: campaign.channels.map((channel) => ({
        channelId: channel.channel_id,
        name: channel.name,
        channelType: channel.channel_type,
        kind: channel.kind,
        status: channel.status,
        startsAt: channel.starts_at,
        endsAt: channel.ends_at,
      })),
    })),
    adSpend: major(data.ad_spend_minor),
    attributedRevenue: major(data.attributed_revenue_minor),
    cpa: major(data.cpa_minor),
    roas: data.roas,
    notAvailable: reasonsFor(data.not_available, {
      ad_spend_minor: 'adSpend',
      attributed_revenue_minor: 'attributedRevenue',
      cpa_minor: 'cpa',
      roas: 'roas',
    }),
  }
}

// --- requests ----------------------------------------------------------------

/** The competition's own path: `/analytics/competitions/{id}`, then the tab's route, if any. */
export function competitionDetailPath(competitionId: string, tab?: 'funnel' | 'sources' | 'devices' | 'advertising'): string {
  const base = `/analytics/competitions/${encodeURIComponent(competitionId)}`
  return tab ? `${base}/${tab}` : base
}

/**
 * The query string every route reads: the Overview's scope (`from`/`to`, `website_id`,
 * the environments), then the route's own parameter. The period is always sent, so the
 * API's 28-day default never applies to a page showing another one; a value the API
 * would refuse is never sent (an unknown model reads as the default).
 */
export function competitionDetailSearch(
  filters: GlobalFilters,
  now: Date,
  extra: { granularity?: SalesGranularity; model?: AttributionModel } = {},
): string {
  const params = new URLSearchParams(overviewSearch(filters, now))
  if (extra.granularity && (SALES_GRANULARITIES as readonly string[]).includes(extra.granularity)) {
    params.set('granularity', extra.granularity)
  }
  if (extra.model) {
    params.set('model', (ATTRIBUTION_MODELS as readonly string[]).includes(extra.model) ? extra.model : DEFAULT_SOURCES_MODEL)
  }
  return params.toString()
}
